import { useState } from 'react'
import { AuthChrome } from '../components/AuthChrome'
import { Button } from '../components/ui'
import { useAuth } from '../contexts/AuthContext'
import type { AccountStatus as Status } from '../lib/problem'

/**
 * The screen a non-active account sees instead of the product.
 *
 * This is the visible half of Sprint 1.3's approval gate, and it does not exist
 * in AIVY at all (§3.5). Five statuses reach it, not one: the backend derives
 * the Problem code from the status (`errors.py:277`), so `pending_approval`,
 * `suspended`, `rejected`, `deleted` and `purged` are each a distinct refusal
 * and each needs its own words.
 *
 * **No retention dates are shown**, and that is deliberate rather than an
 * omission. `MeResponse` carries neither `deleted_at` nor `purge_after` —
 * `contracts/user.py` hides both from the caller on purpose — so the only
 * honest thing this screen can say about timing is that support can tell them.
 * Inventing "30 days" here would be a compliance claim the client cannot see.
 *
 * Voice per the brand guide: calm, plain, no shame mechanics, no blame, and
 * always a next action even when that action is "write to us".
 */

interface Copy {
  eyebrow: string
  title: string
  body: string
  detail?: string
  tone: 'waiting' | 'blocked'
}

const COPY: Record<Exclude<Status, 'active'>, Copy> = {
  pending_approval: {
    eyebrow: 'Account status',
    title: 'Your account is waiting for approval',
    body: 'Your email is verified and your account exists. An administrator reviews each new account before it can be used, so there is nothing further for you to do right now.',
    detail: 'You will be able to sign in and start building your baseline as soon as it is approved.',
    tone: 'waiting',
  },
  suspended: {
    eyebrow: 'Account status',
    title: 'Your account is suspended',
    body: 'An administrator has paused access to this account. Your recordings and your baseline are untouched.',
    detail: 'If you think this is a mistake, reply to the email you signed up with and an administrator will pick it up.',
    tone: 'blocked',
  },
  rejected: {
    eyebrow: 'Account status',
    title: 'This account was not approved',
    body: 'An administrator reviewed the request and did not approve it. BrainSpot is in a controlled pilot, so access is limited rather than open.',
    detail: 'If you were expecting access, reply to the email you signed up with.',
    tone: 'blocked',
  },
  deleted: {
    eyebrow: 'Account status',
    title: 'This account is scheduled for deletion',
    body: 'Deletion was requested for this account, so it can no longer be used. Its data is held for a retention period and then permanently erased.',
    detail: 'If this was not what you intended, contact us — an administrator can restore it while the retention period is still running.',
    tone: 'blocked',
  },
  purged: {
    eyebrow: 'Account status',
    title: 'This account no longer exists',
    body: 'The data behind this account has been permanently erased. This cannot be undone, and there is nothing left to restore.',
    detail: 'You are welcome to create a new account if you would like to start again.',
    tone: 'blocked',
  },
}

export function AccountStatusScreen({ status }: { status: Exclude<Status, 'active'> }) {
  const { account, refreshAccount, signOut } = useAuth()
  const copy = COPY[status]
  const [refreshFailed, setRefreshFailed] = useState(false)
  const verifiedPending = status === 'pending_approval' && account?.email_verified === true
  // R69: /me says the email is not verified, so the verified body's first
  // claim cannot be made. Say that fact, the review step and a way to get help,
  // without repeating the title.
  const lead = status === 'pending_approval' && !verifiedPending
    ? 'Your email address is not verified yet, and an administrator reviews each new account before it can be used. If you need help, reply to the email you signed up with.'
    : copy.body
  const checkAgain = async () => {
    try { setRefreshFailed((await refreshAccount()) === null) }
    catch { setRefreshFailed(true) }
  }

  return (
    <AuthChrome eyebrow={copy.eyebrow} title={copy.title} lead={lead}>
      <div className={`account-gate account-gate--${copy.tone}`}>
        {verifiedPending && (
          <ol className="account-gate__timeline" aria-label="Account approval progress">
            <li className="account-gate__step account-gate__step--done">Signed up <span>Complete</span></li>
            <li className="account-gate__step account-gate__step--done">Email verified <span>Complete</span></li>
            <li className="account-gate__step account-gate__step--current" aria-current="step">Approval <span>In progress</span></li>
            <li className="account-gate__step">Ready <span>Pending</span></li>
          </ol>
        )}
        {copy.detail && <p className="status-card__detail">{copy.detail}</p>}
        {account?.email && <p className="status-card__who">
          Signed in as <span className="status-card__email">{account.email}</span>
        </p>}
        {refreshFailed && <p role="alert">We could not refresh your account. Please try again or sign in again.</p>}
        <div className="status-card__actions">
          {status === 'pending_approval' && (
            <Button variant="secondary" onClick={() => void checkAgain()}>
              Check again
            </Button>
          )}
          <Button variant="ghost" onClick={() => void signOut()}>Sign out</Button>
        </div>
      </div>
    </AuthChrome>
  )
}
